import { debounce } from 'lodash';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';

import Iconify from 'src/components/iconify';

import {
  ILoanTableFilters,
  ILoanTableFilterValue,
  LOAN_BORROWER_TYPE_OPTIONS,
} from 'src/types/loan-agreement';

type Props = {
  filters: ILoanTableFilters;
  onFilters: (name: string, value: ILoanTableFilterValue) => void;
  onResetFilters: VoidFunction;
  canReset: boolean;
  showOverdueFilter: boolean;
};

export default function LoanAgreementTableToolbar({
  filters,
  onFilters,
  onResetFilters,
  canReset,
  showOverdueFilter,
}: Props) {
  const [input, setInput] = useState(filters.generalSearch);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => onFilters('generalSearch', value), 500),
    [onFilters]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInput(event.target.value);
    debouncedSearch(event.target.value);
  };

  const clearSearch = () => {
    setInput('');
    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    onResetFilters();
  }, [onResetFilters]);

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{ xs: 'column', md: 'row' }}
      sx={{ p: 2.5, pr: { xs: 2.5, md: 1 } }}
    >
      <TextField
        fullWidth
        value={input}
        onChange={handleSearchChange}
        placeholder="Search by name, NIC, phone number or employee ID..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
          endAdornment:
            input.length > 0 ? (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch}>
                  <Iconify icon="eva:close-fill" color="text.disabled" />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
        inputProps={{ autoComplete: 'off' }}
      />

      <FormControl sx={{ flexShrink: 0, width: { xs: 1, md: 180 } }}>
        <InputLabel>Borrower</InputLabel>
        <Select
          value={filters.borrowerType}
          onChange={(event) => onFilters('borrowerType', event.target.value)}
          input={<OutlinedInput label="Borrower" />}
        >
          <MenuItem value="all">All</MenuItem>
          {LOAN_BORROWER_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showOverdueFilter && (
        <FormControlLabel
          sx={{ flexShrink: 0, mr: 0 }}
          control={
            <Switch
              checked={filters.overdue}
              onChange={(event) => onFilters('overdue', event.target.checked)}
            />
          }
          label="Overdue only"
        />
      )}

      {canReset && (
        <Button
          color="error"
          onClick={clearAll}
          sx={{ textDecoration: 'underline', flexShrink: 0 }}
        >
          Clear
        </Button>
      )}
    </Stack>
  );
}
